import { useCallback, useMemo } from 'react';
import { registerMutation, registerQuery, useQuery, useMutation } from 'academy-common-lib/utils/DataFetchingClient';
import { BASE_SERVICE_URI } from '../../constants/ApiConstants';
import http from 'hub-http/clients/apiClient';
import { useAllProgress } from '../userProgress/userProgressQueries';
import { useAllLessons } from '../lessons/lessonQueries';
import { useAllTracks } from '../tracks/tracksQueries';
import { categoryToSearchQuery, toolToSearchQuery } from './searchTypes';
import { useLegacyCatalogSearchParams } from './searchHooks';
import sortBy from 'hs-lodash/sortBy';
import { FILTER_CONSTS, MarketingHubTools, SalesHubTools, ServiceHubTools } from '../../constants/TopFilterConstants';
import { marketingCategories, salesCategories, serviceCategories, webCategories } from 'academy-common-lib/constants/categories';
import getLang from 'LearningCenterUI/utils/getLang';
import Raven from 'raven-js';
import { useAllShorts } from '../shortContent/shortContentQueries';
import { SEARCH_QUERY, SEND_FEEDBACK_MUTATION } from '../../../__generated__/chirp/com/hubspot/learning/center/search/rpc/SearchChirpService.dfc';
import { useHasGate } from '../accounts';
import { useGetAllHubspotPlaylists } from '../hubspotPlaylists/hubspotPlaylistQueries';
import { useSearchRecencyScoringConfig } from './searchExperiments';
const SEARCH_URI = `${BASE_SERVICE_URI}/search/v1`;
export const SEND_SEARCH_FEEDBACK = registerMutation({
  fieldName: 'sendSearchFeedback',
  args: ['correlationId', 'resultMLOptionId'],
  fetcher({
    correlationId,
    resultMLOptionId,
    contentContainerType
  }) {
    return http.post(`${SEARCH_URI}/feedback`, {
      data: {
        correlationId,
        resultMLOptionId,
        contentContainerType
      }
    });
  }
});
const useSendSearchFeedback = ({
  correlationId
}) => {
  const [mutate, {
    error: sendSearchFeebackError,
    loading: sendSearchFeedbackLoading
  }] = useMutation(SEND_SEARCH_FEEDBACK);
  const sendSearchFeedback = useCallback(({
    resultMLOptionId,
    contentContainerType
  }) => {
    return mutate({
      variables: {
        correlationId,
        resultMLOptionId,
        contentContainerType
      },
      onError(error) {
        Raven.captureException(error);
      }
    });
  }, [correlationId, mutate]);
  return {
    sendSearchFeedback,
    sendSearchFeebackError,
    sendSearchFeedbackLoading
  };
};
export function replaceCategoriesWithSubs(categories) {
  return categories.map(category => {
    if (category === 'MARKETING') {
      return marketingCategories;
    }
    if (category === 'SALES') {
      return salesCategories;
    }
    if (category === 'SERVICE' || category === 'SERVICE_SUPPORT') {
      return serviceCategories;
    }
    if (category === 'WEB_DESIGN_DEVELOPMENT') {
      return webCategories;
    }
    return category;
  }).flat();
}
export function replaceHubWithTools(tools) {
  return tools.map(tool => {
    if (tool === 'MARKETING_HUB_TOOLS') {
      return MarketingHubTools.toJS();
    }
    if (tool === 'SALES_HUB_TOOLS') {
      return SalesHubTools.toJS();
    }
    if (tool === 'SERVICE_HUB_TOOLS') {
      return ServiceHubTools.toJS();
    }
    return tool;
  }).flat();
}
const GET_SEARCH = registerQuery({
  fieldName: 'catalogSearchResults',
  args: ['durations', 'languages', 'categories', 'hubSpotSoftware', 'levels', 'contentTypes', 'search', 'partnersOnly', 'pageNumber', 'pageSize'],
  fetcher({
    durations,
    languages,
    categories,
    hubSpotSoftware,
    levels,
    contentTypes,
    search,
    partnersOnly,
    pageNumber = 1,
    pageSize = 10000
  }) {
    return http.get(SEARCH_URI, {
      query: {
        durations,
        languages,
        categories,
        hubSpotSoftware,
        levels,
        contentTypes,
        search,
        partnersOnly,
        pageSize,
        pageNumber
      }
    }).then(data => data);
  }
});
export const useBaseSearch = ({
  skip,
  durations,
  languages,
  categories,
  hubSpotSoftware,
  levels,
  contentTypes,
  search,
  partnersOnly,
  pageNumber,
  pageSize
}) => {
  const getSearchTermFromCategory = () => {
    return categories && categories.length && categoryToSearchQuery[categories[0]] || hubSpotSoftware && hubSpotSoftware.length && toolToSearchQuery[hubSpotSoftware[0]] || '';
  };
  const {
    data,
    loading: searchLoading,
    error: searchError,
    fetchMore: fetchMoreSearchResults
  } = useQuery(GET_SEARCH, {
    variables: {
      durations,
      languages,
      categories: replaceCategoriesWithSubs(categories || []),
      hubSpotSoftware: replaceHubWithTools(hubSpotSoftware || []),
      levels,
      contentTypes,
      search: search || getSearchTermFromCategory(),
      partnersOnly,
      pageNumber,
      pageSize
    },
    skip
  });
  const {
    sendSearchFeedback,
    sendSearchFeebackError,
    sendSearchFeedbackLoading
  } = useSendSearchFeedback({
    correlationId: data && data.correlationId || ''
  });
  return {
    searchResults: data ? data.results : undefined,
    totalSearchResults: data ? data.total : undefined,
    searchLoading,
    searchError,
    fetchMoreSearchResults,
    sendSearchFeedback,
    sendSearchFeebackError,
    sendSearchFeedbackLoading
  };
};
const mustPaginateLocally = true;
export const useCatalogSearch = ({
  durations,
  languages = [getLang()],
  categories,
  hubSpotSoftware,
  levels,
  contentTypes,
  search,
  partnersOnly,
  skip = false,
  pageNumber,
  pageSize,
  removeDeprecated = false
} = {}) => {
  const language = languages[0];
  const {
    allProgress,
    allProgressLoading,
    allProgressError
  } = useAllProgress({
    language: languages[0]
  });
  const {
    allLessons,
    allLessonsLoading
  } = useAllLessons(language);
  const {
    allTracks,
    allTracksLoading
  } = useAllTracks(language);
  const {
    allShorts,
    allShortsLoading
  } = useAllShorts();
  const {
    allHubspotPlaylists,
    allHubspotPlaylistsLoading
  } = useGetAllHubspotPlaylists();
  const {
    hasGate: isUngatedToLearningPaths
  } = useHasGate('Academy:LearningPaths');
  const {
    searchResults,
    totalSearchResults,
    searchLoading,
    searchError,
    fetchMoreSearchResults,
    sendSearchFeebackError,
    sendSearchFeedbackLoading,
    sendSearchFeedback
  } = useBaseSearch({
    skip,
    durations,
    languages,
    categories,
    hubSpotSoftware,
    levels,
    contentTypes,
    search,
    partnersOnly,
    pageNumber,
    pageSize: mustPaginateLocally ? undefined : pageSize
  });
  const catalogSearchData = useMemo(() => (searchResults || []).map(item => {
    const trackLessonData = item.contentType === 'SHORT_FORM_CONTENT' ? (allShorts === null || allShorts === void 0 ? void 0 : allShorts.find) && (allShorts === null || allShorts === void 0 ? void 0 : allShorts.find(({
      contentId
    }) => contentId === item.id)) : item.contentType === 'TRACK' ? allTracks[item.id] : item.contentType === 'LEARNING_PATH' && isUngatedToLearningPaths ? allHubspotPlaylists[item.id] : item.contentType === 'LEARNING_PATH' && !isUngatedToLearningPaths ? null : allLessons[item.id];
    return Object.assign({}, item, {
      data: trackLessonData || undefined
    });
  }).filter(item => {
    // remove result if no data attached
    if (!item.data) return false;
    // @ts-expect-error deprecated doesnt exist on lesson
    if (removeDeprecated && item.data.deprecated) {
      // remove result if deprecated
      return false;
    }
    // we do not want to display content that it is not in the user's language, the specified (filter) language or the fallback language (english))
    if (![language || getLang(), 'en'].includes(item.data.language.toString().toLowerCase())) {
      return false;
    }
    return true;
  }).map(item => {
    let status = 'NOT_STARTED';
    if (allProgress) {
      const basicItemType = item.contentType === 'SHORT_FORM_CONTENT' ? 'SHORT_FORM_CONTENT' : item.contentType === 'TRACK' ? 'TRACK' : 'LESSON';
      const itemProgress = allProgress.assetProgress.find(asset => asset.id === item.id && asset.type === basicItemType);
      status = itemProgress ? itemProgress.status : 'NOT_STARTED';
      return Object.assign({}, item, itemProgress, {
        status
      });
    }
    return Object.assign({}, item, {
      status
    });
  }), [allLessons, allProgress, allShorts, allTracks, allHubspotPlaylists, language, removeDeprecated, searchResults, isUngatedToLearningPaths]);
  return {
    catalogSearchLoading: searchLoading || allProgressLoading || allTracksLoading || allLessonsLoading || allShortsLoading || isUngatedToLearningPaths && allHubspotPlaylistsLoading,
    catalogSearchData,
    catalogTotalSearchResults: totalSearchResults,
    catalogSearchError: searchError || allProgressError,
    fetchMoreCatalogSearchResults: fetchMoreSearchResults,
    sendSearchFeebackError,
    sendSearchFeedbackLoading,
    sendSearchFeedback,
    mustPaginateLocally
  };
};
const isTrack = item => item.certification !== undefined;
export const useSearchParamCatalogSearch = ({
  isPartnerPage = false,
  sortByDefault = false,
  status: statusProp,
  context,
  pageSize,
  contentTypes: contentTypesProp,
  tools: toolsProp,
  skip,
  removeDeprecated = true,
  language
} = {}) => {
  const {
    searchQuery,
    selectedCategories,
    selectedDurations,
    selectedTools,
    selectedLevels,
    selectedContentTypes,
    selectedLanguages,
    selectedAwardTypes,
    selectedSortBys,
    pageNumber,
    progressStatus
  } = useLegacyCatalogSearchParams();
  const contentTypesSearchParam = selectedContentTypes.map(type => type === 'COURSE' ? 'TRACK' : type);

  // content types and progress status filters from search query has priority over prop filters
  const contentTypes = contentTypesSearchParam !== null && contentTypesSearchParam !== void 0 && contentTypesSearchParam.length ? contentTypesSearchParam : contentTypesProp !== null && contentTypesProp !== void 0 ? contentTypesProp : [];
  const status = progressStatus !== null && progressStatus !== void 0 && progressStatus.length ? [progressStatus] : statusProp;
  const searchQueryParams = {
    durations: selectedDurations,
    languages: language ? [language] : selectedLanguages.map(l => l.split('-')[0].toLowerCase()),
    categories: selectedCategories,
    hubSpotSoftware: toolsProp || selectedTools,
    levels: selectedLevels,
    contentTypes,
    search: searchQuery.length && searchQuery[0].length ? searchQuery[0] : '',
    partnersOnly: isPartnerPage,
    pageNumber,
    pageSize,
    skip,
    removeDeprecated
  };
  const {
    catalogSearchData,
    catalogSearchLoading,
    catalogSearchError,
    fetchMoreCatalogSearchResults,
    sendSearchFeebackError,
    sendSearchFeedbackLoading,
    sendSearchFeedback
  } = useCatalogSearch(searchQueryParams);
  const sortFilterData = queryData => {
    let transformedData = queryData;
    switch (selectedSortBys[0]) {
      case FILTER_CONSTS.SORT_BYS.RECENTLY_ACCESSED:
        transformedData = sortBy(sortBy(transformedData, i => i.data.name), i => -(i.updatedAt || 0));
        break;
      case FILTER_CONSTS.SORT_BYS.TITLE_ASC:
        transformedData = sortBy(transformedData, i => i.data.name);
        break;
      case FILTER_CONSTS.SORT_BYS.TITLE_DESC:
        transformedData = sortBy(transformedData, i => i.data.name).reverse();
        break;
      default:
        if (sortByDefault) {
          transformedData = sortBy(sortBy(transformedData, i => i.data.name), i => -(i.updatedAt || 0));
        }
    }
    if (selectedAwardTypes.includes('ONLY_SHOW_CERTIFICATE_COURSES')) {
      transformedData = transformedData.filter(({
        data
      }) => isTrack(data) && data.certification);
    }
    if (status) {
      transformedData = transformedData.filter(item => status.includes(item.status) || status.includes('COMPLETED') && item.status === 'EXPIRING_SOON');
    }
    if (context) {
      transformedData = transformedData.filter(item => item.context ? item.context === context : true);
    }
    return transformedData;
  };
  const sortedFilteredData = sortFilterData(catalogSearchData);
  return {
    tracksLessonsSearchQueryLoading: catalogSearchLoading,
    tracksLessonsSearchQueryError: catalogSearchError,
    tracksLessonsSearchQueryData: sortedFilteredData,
    tracksLessonsSearchResultsTotal: sortedFilteredData.length,
    searchQueryParams,
    fetchMoreCatalogSearchResults,
    sendSearchFeebackError,
    sendSearchFeedbackLoading,
    sendSearchFeedback,
    mustPaginateLocally
  };
};
export const useSearch = ({
  variables,
  skip
}) => {
  const {
    scoringConfig,
    isLoadingSearchRecencyScoringConfig
  } = useSearchRecencyScoringConfig();
  const {
    data: searchResults,
    loading: searchResultsLoading,
    error: searchResultsError
  } = useQuery(SEARCH_QUERY, {
    variables: Object.assign({}, variables, {
      scoringConfig
    }),
    skip: skip || isLoadingSearchRecencyScoringConfig
  });
  return {
    searchResults: searchResults === null || searchResults === void 0 ? void 0 : searchResults.results,
    searchResultsLoading: searchResultsLoading || isLoadingSearchRecencyScoringConfig,
    searchResultsError,
    totalCount: (searchResults === null || searchResults === void 0 ? void 0 : searchResults.total) || 0,
    correlationId: searchResults === null || searchResults === void 0 ? void 0 : searchResults.correlationId
  };
};
export const useSearchFeedback = () => {
  const [mutate, {
    data: sendSearchFeedbackResponse,
    loading: sendSearchFeedbackLoading,
    error: sendSearchFeedbackError
  }] = useMutation(SEND_FEEDBACK_MUTATION, {});
  const sendSearchFeedback = useCallback(request => mutate({
    variables: request
  }), [mutate]);
  return {
    sendSearchFeedback,
    sendSearchFeedbackResponse,
    sendSearchFeedbackLoading,
    sendSearchFeedbackError
  };
};